// 頁面盤點表：由 docs/legacy-candidates.json（Gate 1 盤點，156 筆候選）轉出的最小子集。
//
// 只留假資料層真正用得到的三個欄位：pageId（＝ DataSource 的 resource）、module、
// 以及由 pageType 對應出來的資料形狀。legacyUrl／permissionChain／modalCandidates 等
// 欄位與假資料無關，不搬進 bundle。
//
// **不要手改這個檔**：內容必須與 docs/legacy-candidates.json 一致，
// fixtures.test.ts 的「page-catalog 與 Gate 1 盤點表一致」會逐筆比對，
// 盤點表更新後照著錯誤訊息重新產生即可。
//
// ponytail: pageId 的前綴**不能**用來推 module（實測 156 筆裡有 89 筆對不上，
// 例如 customer-tsgh-high-risk-report 屬於 user 模組），所以這張對照表是必要的資料，
// 不是可以用規則取代的樣板。反過來說，pageType 只用來選「形狀」，
// 不為 149 個頁面各寫一份 fixture。
//
// pageType → shape 對應（依 legacy-candidates.json 的 statePresets 分類）：
//   一般 CRUD 清單 → list      報表／查詢 → report
//   複雜表單       → form      純顯示頁／登入頁／redirect → display
//   未定           → list（先當清單處理，S2 盤點確認後再改）

/** 假資料形狀。刻意只有四種，不是每頁一種。 */
export type PageShape = 'list' | 'report' | 'form' | 'display';

export interface PageMeta {
  /** 模組 slug，對應 legacy-candidates.json 的 candidates[].module。 */
  readonly module: string;
  readonly shape: PageShape;
}

/** 模組 slug → 選單上的中文模組名稱（原系統既有固定文字，照抄不改寫）。 */
export const moduleLabels: Readonly<Record<string, string>> = {
  announcement: '公告',
  'co-edit-form': '共同編輯表單專區',
  config: '系統資料管理',
  course: '課程專區',
  discussion: '留言板',
  errors: '錯誤頁',
  exam: '測驗中心',
  'form-template': '表單精靈',
  home: '首頁',
  'instruction-plan': '訓練計畫專區',
  'job-type-config': '訓練類別管理',
  'leave-form': '請假專區',
  login: '登入',
  profile: '個人資料管理',
  redirects: '純轉向',
  report: '報表管理',
  study: '學習紀錄',
  'study-passport': '學習歷程下載',
  system: '系統管理',
  train: '學員專區',
  transcript: '成績單',
  unresolved: '待決候選清單',
  user: '人員管理',
  vote: '優良臨床教師投票專區',
};

/** pageId → 假資料需要的中繼資料。resource 字串就是 pageId。 */
export const pageCatalog: Readonly<Record<string, PageMeta>> = {
  'announcement-announcement-list': { module: 'announcement', shape: 'list' },
  'common-html-output': { module: 'unresolved', shape: 'list' },
  'config-account-v-2': { module: 'config', shape: 'list' },
  'config-annual': { module: 'config', shape: 'list' },
  'config-comment': { module: 'profile', shape: 'list' },
  'config-department': { module: 'config', shape: 'list' },
  'config-device': { module: 'config', shape: 'list' },
  'config-forbid-word': { module: 'config', shape: 'list' },
  'config-job-title': { module: 'config', shape: 'list' },
  'config-job-type': { module: 'job-type-config', shape: 'display' },
  'config-job-type-department': { module: 'job-type-config', shape: 'list' },
  'config-location': { module: 'config', shape: 'list' },
  'config-organization': { module: 'config', shape: 'list' },
  'config-role': { module: 'config', shape: 'list' },
  'course-clazz-list': { module: 'course', shape: 'list' },
  'course-clazz-query-list': { module: 'course', shape: 'list' },
  'course-clazz-query-list-class-query': { module: 'course', shape: 'list' },
  'course-clazz-query-list-js': { module: 'unresolved', shape: 'list' },
  'course-course-manage': { module: 'course', shape: 'list' },
  'course-course-static-list': { module: 'report', shape: 'display' },
  'course-course-type': { module: 'course', shape: 'list' },
  'customer-tsgh-case-report': { module: 'train', shape: 'report' },
  'customer-tsgh-cert-template-cfg': { module: 'config', shape: 'display' },
  'customer-tsgh-clert-cerk': { module: 'train', shape: 'display' },
  'customer-tsgh-core-curriculum-report': { module: 'train', shape: 'report' },
  'customer-tsgh-course-record-report': { module: 'train', shape: 'report' },
  'customer-tsgh-draw-board': { module: 'unresolved', shape: 'list' },
  'customer-tsgh-high-risk-doctor-report': { module: 'user', shape: 'report' },
  'customer-tsgh-high-risk-report': { module: 'user', shape: 'report' },
  'customer-tsgh-high-risk-setting': { module: 'user', shape: 'display' },
  'customer-tsgh-high-risk-setting-report': { module: 'user', shape: 'report' },
  'customer-tsgh-service-standard-cfg-report': { module: 'user', shape: 'report' },
  'customer-tsgh-service-standard-report': { module: 'user', shape: 'report' },
  'customer-tsgh-service-standard-setting': { module: 'user', shape: 'display' },
  'customer-tsgh-tch-satisfaction-survey-report': { module: 'user', shape: 'report' },
  'customer-tsgh-to-high-risk-form': { module: 'user', shape: 'form' },
  'customer-tsgh-to-service-standard-form': { module: 'user', shape: 'form' },
  'customer-tsgh-tsgh-student-course-report': { module: 'train', shape: 'report' },
  'customer-tsgh-tsgh-study-history-data': { module: 'user', shape: 'form' },
  'customer-tsgh-tsgh-study-history-list': { module: 'study-passport', shape: 'display' },
  'discuss-discuss-board': { module: 'discussion', shape: 'list' },
  'discuss-discuss-type': { module: 'discussion', shape: 'list' },
  'error-401': { module: 'errors', shape: 'display' },
  'error-403': { module: 'errors', shape: 'display' },
  'error-404': { module: 'errors', shape: 'display' },
  'error-405': { module: 'errors', shape: 'display' },
  'error-500': { module: 'errors', shape: 'display' },
  'error-error': { module: 'errors', shape: 'display' },
  'exam-dispatch-exam': { module: 'exam', shape: 'form' },
  'exam-exam-manage': { module: 'exam', shape: 'list' },
  'exam-init-all-question': { module: 'exam', shape: 'form' },
  'exam-question-bank': { module: 'exam', shape: 'list' },
  'exam-question-bank-type': { module: 'exam', shape: 'list' },
  'exam-question-manage': { module: 'exam', shape: 'list' },
  'exam-testing-manage': { module: 'exam', shape: 'list' },
  'exam-view-testing': { module: 'exam', shape: 'form' },
  'exam-view-testing-view-testing': { module: 'exam', shape: 'form' },
  'form-all-form-list': { module: 'train', shape: 'form' },
  'form-class-form-list': { module: 'course', shape: 'list' },
  'form-co-edit-form-completion-statistics-page': { module: 'co-edit-form', shape: 'form' },
  'form-co-edit-form-export-page': { module: 'co-edit-form', shape: 'form' },
  'form-co-edit-form-fill-form': { module: 'co-edit-form', shape: 'form' },
  'form-co-edit-form-form-generator': { module: 'co-edit-form', shape: 'form' },
  'form-co-edit-form-query-page': { module: 'co-edit-form', shape: 'form' },
  'form-fill-form': { module: 'train', shape: 'form' },
  'form-form-static-list': { module: 'report', shape: 'form' },
  'form-plan-form-batch-send': { module: 'train', shape: 'form' },
  'form-plan-form-dispatch': { module: 'instruction-plan', shape: 'display' },
  'form-plan-form-list': { module: 'train', shape: 'list' },
  'form-print-forms': { module: 'train', shape: 'form' },
  'form-template-common-form-template': { module: 'form-template', shape: 'list' },
  'form-template-field-type-list': { module: 'form-template', shape: 'form' },
  'form-template-form-preview': { module: 'form-template', shape: 'form' },
  'form-template-form-template': { module: 'form-template', shape: 'form' },
  'form-template-form-template-list': { module: 'form-template', shape: 'display' },
  'form-template-form-template-list-process-form-fields': {
    module: 'form-template',
    shape: 'display',
  },
  'form-template-set-form-fields': { module: 'form-template', shape: 'list' },
  'form-template-set-form-flow': { module: 'form-template', shape: 'form' },
  index: { module: 'login', shape: 'display' },
  'instruction-course-department-code': { module: 'course', shape: 'list' },
  'instruction-personalization-gpa-show-version': { module: 'form-template', shape: 'list' },
  'instruction-personalization-gpa-score-setting': { module: 'form-template', shape: 'display' },
  'instruction-plan-target-plan-target-list': { module: 'instruction-plan', shape: 'list' },
  'instruction-study-item-maintain': { module: 'instruction-plan', shape: 'list' },
  'instruction-study-item-type-maintain': { module: 'instruction-plan', shape: 'list' },
  'instruction-teacher-pool-list': { module: 'form-template', shape: 'list' },
  'leave-leave-annual': { module: 'leave-form', shape: 'list' },
  'leave-leave-form': { module: 'leave-form', shape: 'list' },
  'leave-leave-holiday': { module: 'leave-form', shape: 'display' },
  'leave-leave-type': { module: 'leave-form', shape: 'list' },
  'main-dentisit-student-main': { module: 'home', shape: 'list' },
  'main-dentisit-teacher-main': { module: 'home', shape: 'list' },
  'main-dept-admin-main': { module: 'home', shape: 'list' },
  'main-main': { module: 'home', shape: 'list' },
  'main-medical-affairs-clinical-main': { module: 'unresolved', shape: 'list' },
  'main-medical-affairs-student-main': { module: 'home', shape: 'list' },
  'main-medical-affairs-teacher-main': { module: 'home', shape: 'list' },
  'main-new-main-exam-list': { module: 'exam', shape: 'list' },
  'main-new-main-study-record': { module: 'study', shape: 'list' },
  'main-student-main': { module: 'home', shape: 'list' },
  'main-teacher-main': { module: 'home', shape: 'list' },
  'profile-profile-maintenance': { module: 'profile', shape: 'list' },
  'report-common-class-form-export': { module: 'course', shape: 'report' },
  'report-common-clazz-attendance-export': { module: 'report', shape: 'report' },
  'report-common-clazz-detail-export': { module: 'report', shape: 'report' },
  'report-common-plan-form-export': { module: 'train', shape: 'report' },
  'report-pgywm-all-student-scores': { module: 'report', shape: 'report' },
  'report-tsgh-academic-mom': { module: 'report', shape: 'report' },
  'report-tsgh-class-form-static': { module: 'report', shape: 'report' },
  'report-tsgh-class-form-static-to-class-form-7': { module: 'report', shape: 'report' },
  'report-tsgh-core-skill-form-report': { module: 'report', shape: 'report' },
  'report-tsgh-core-skill-report-by-form': { module: 'report', shape: 'report' },
  'report-tsgh-core-skill-report-by-student': { module: 'report', shape: 'report' },
  'report-tsgh-core-skill-statistic': { module: 'report', shape: 'report' },
  'report-tsgh-core-skill-statistic-report': { module: 'report', shape: 'report' },
  'report-tsgh-core-skill-sutdent-report': { module: 'report', shape: 'report' },
  'report-tsgh-gpa-form-report': { module: 'report', shape: 'report' },
  'report-tsgh-login-static': { module: 'report', shape: 'report' },
  'report-tsgh-plan-form-static': { module: 'report', shape: 'report' },
  'report-tsgh-plan-form-static-2': { module: 'report', shape: 'report' },
  'report-tsgh-plan-form-static-2-to-plan-form-10': { module: 'report', shape: 'report' },
  'report-tsgh-plan-form-static-2-to-plan-form-9': { module: 'report', shape: 'report' },
  'report-tsgh-plan-form-static-to-plan-form-5': { module: 'report', shape: 'report' },
  'report-ugywm-course-form-finishrate': { module: 'report', shape: 'report' },
  'report-ugywm-student-dept-satisfy': { module: 'report', shape: 'report' },
  'system-about': { module: 'system', shape: 'display' },
  'system-api-log': { module: 'system', shape: 'display' },
  'system-changelog': { module: 'system', shape: 'list' },
  'system-download-log': { module: 'system', shape: 'display' },
  'system-login-log': { module: 'system', shape: 'display' },
  'system-message-config': { module: 'system', shape: 'display' },
  'system-message-config-save-message-config': { module: 'system', shape: 'display' },
  'system-system-config': { module: 'system', shape: 'display' },
  'system-system-config-save-system-config': { module: 'system', shape: 'display' },
  'training-arrange-train': { module: 'train', shape: 'form' },
  'training-education-training': { module: 'home', shape: 'display' },
  'training-education-training-student': { module: 'home', shape: 'display' },
  'training-permission-manage-student': { module: 'train', shape: 'form' },
  'training-permission-manage-teacher': { module: 'user', shape: 'display' },
  'training-plan-inst-clinical-arrange': { module: 'user', shape: 'display' },
  'training-plan-inst-plan-inst-list': { module: 'train', shape: 'list' },
  'training-study-passport-study-passport': { module: 'train', shape: 'form' },
  'training-study-passport-study-passport-list': { module: 'train', shape: 'display' },
  'training-study-record-error-content': { module: 'study', shape: 'list' },
  'training-study-record-show-study-record': { module: 'study', shape: 'form' },
  'training-study-record-study-record-fill': { module: 'study', shape: 'list' },
  'training-study-record-study-record-form-content': { module: 'study', shape: 'form' },
  'training-study-record-study-record-form-content-show-study-record-form': {
    module: 'study',
    shape: 'form',
  },
  'training-study-record-study-record-status': { module: 'study', shape: 'list' },
  'training-study-record-study-record-verify': { module: 'study', shape: 'display' },
  'training-transcript-list': { module: 'transcript', shape: 'form' },
  'vote-dept-manager-recommend': { module: 'vote', shape: 'list' },
  'vote-excellent-teacher': { module: 'vote', shape: 'display' },
  'vote-total-vote-analysis-report': { module: 'report', shape: 'report' },
  'vote-total-vote-report': { module: 'report', shape: 'report' },
  'vote-vote-maintenance': { module: 'vote', shape: 'list' },
  'webcontent-index': { module: 'redirects', shape: 'display' },
};
